import React, { useEffect, useMemo, useRef } from 'react';
import { MAPPING_ATYPE } from '../../lib/rcu/catalog/mapping';
import { CONNECTION_STATUS, TOPIC_ROLE } from '../../lib/rcu/catalog/link';
import { addMappings, readMappings } from '../../lib/rcu/link/commands/mappings';
import { refusalOutcome } from '../../lib/rcu/link/dispatch';
import { topicFor } from '../../lib/rcu/link/topics';
import type { RcuDeleteEntry, RcuDocument, RcuSceneDocumentEntry } from '../../lib/rcu/wire';
import type { RcuConfig } from '../../lib/rcu/model';
import type { SendOutcome } from '../../lib/rcu/link/session';
import { KeypadsPanel, useKeypadStore } from '../../features/keypads';
import type { KeypadLink, KeypadPort } from '../../features/keypads';
import { useRcuConfigStore } from '../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../stores/rcuLinkStore';
import { keypadTargets } from './keypadTargets';
import { lastKeypadReply } from './keypadReply';

/**
 * How the keypad feature reaches the device.
 *
 * Every request goes through the same command builders and the same link store
 * the rest of the console uses, so the transport preference, the payload shape
 * and the log all behave identically here. A document the builders refuse never
 * reaches the link and is reported as the refusal it is.
 */
const portFor = (
  config: RcuConfig | null,
  send: (config: RcuConfig | null, document: RcuDocument) => Promise<SendOutcome>,
): KeypadPort => {
  const deliver = (document: RcuDocument): Promise<SendOutcome> => send(config, document);

  /** A document the builders refused never reaches the link: it is reported as is. */
  const build = (entries: readonly RcuSceneDocumentEntry[]): Promise<SendOutcome> => {
    const document = addMappings(entries);
    return document.ok ? deliver(document.value) : Promise.resolve(refusalOutcome(document.issue));
  };

  return {
    sendMapping: (entries) => build(entries),
    readMapping: (trigger) => {
      const document = readMappings([trigger]);
      return document.ok ? deliver(document.value) : Promise.resolve(refusalOutcome(document.issue));
    },
    eraseMappings: (triggers) =>
      build(triggers.map((x): RcuDeleteEntry => ({ atype: MAPPING_ATYPE.DELETE, x }))),
    pause: (milliseconds) =>
      new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
      }),
  };
};

/**
 * The keypad console tab.
 *
 * Three jobs, all of them seams: it hands the feature the outputs the current
 * configuration offers, it hands it back every mapping the link brings in, and it
 * hands it the device's own last word about a mapping — as text, because the
 * firmware does not say which command a reply belongs to. The feature never sees
 * the configuration or the link, which is why it can be opened, edited and stored
 * with neither of them working.
 */
export const KeypadsTab: React.FC = () => {
  const config = useRcuConfigStore((store) => store.config);
  const send = useRcuLinkStore((store) => store.send);
  const mappings = useRcuLinkStore((store) => store.live.mappings);
  const mappingsAt = useRcuLinkStore((store) => store.live.mappingsAt);
  const link = useRcuLinkStore((store) => store.link);
  const connection = link.status;
  const events = useRcuLinkStore((store) => store.events);
  const noteReading = useKeypadStore((store) => store.noteReading);
  const forgetAnswers = useKeypadStore((store) => store.forgetAnswers);

  const targets = useMemo(() => keypadTargets(config), [config]);
  /** Everything the device has said about mappings, newest last, read as one. */
  const reply = useMemo(() => lastKeypadReply(events), [events]);
  /** Whether this tablet is subscribed to the topic those answers arrive on. */
  const listening = connection === CONNECTION_STATUS.ONLINE;
  const serial = config?.rcu.serialNumber.trim() ?? '';
  const linkState = useMemo<KeypadLink>(
    () => ({
      answerTopic: serial === '' ? '' : topicFor(serial, TOPIC_ROLE.EVENTS),
      listening,
      trouble: listening ? '' : link.detail,
    }),
    [link.detail, listening, serial],
  );
  const port = useMemo(() => portFor(config, send), [config, send]);

  /** Each reply the link folds in is pushed into the feature's own answers. */
  useEffect(() => {
    for (const reading of mappings) noteReading(reading);
  }, [mappingsAt, mappings, noteReading]);

  /**
   * What the device was asked last session says nothing about this one: a
   * trigger is "not read yet" again the moment the link comes up, so the panel
   * never shows a reading from a tablet that has since been carried away.
   */
  const wasOnline = useRef(false);
  useEffect(() => {
    const online = connection === CONNECTION_STATUS.ONLINE;
    if (online && !wasOnline.current) forgetAnswers();
    wasOnline.current = online;
  }, [connection, forgetAnswers]);

  return <KeypadsPanel targets={targets} port={port} reply={reply} link={linkState} />;
};

export default KeypadsTab;
